import { useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { aggregatorPortalApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { offerInviteToOpenTab } from '../auth/inviteHandoff'
import { OnboardingShell } from '../components/OnboardingShell'
import { GuardianRegisterForm } from './register/GuardianRegisterForm'
import { InstitutionRegisterForm } from './register/InstitutionRegisterForm'
import { INSTITUTION_CONFIG } from './register/institutionConfig'
import { RoleSelectStep, type RegisterRole } from './register/RoleSelectStep'
import { StudentRegisterForm } from './register/StudentRegisterForm'
import { TutorRegisterForm } from './register/TutorRegisterForm'
import { usePageMeta } from '../hooks/usePageMeta'

export function RegisterPage() {
  usePageMeta(
    'Join YellowDots | Register as a Learner or Educator',
    'Create your free YellowDots account to find tutors, coaches and trainers near you, or register as an educator to be discovered by learners in your neighbourhood.',
  )
  const [searchParams] = useSearchParams()
  const referralCode = searchParams.get('ref') ?? undefined
  // A guardian invite token from the email we sent the parent.
  const inviteToken = searchParams.get('invite') ?? undefined
  // An aggregator's onboarding link or QR - /register?agg=<code>. The account is linked to that
  // aggregator; an unknown code is simply ignored, by the server as here.
  const aggregatorCode = searchParams.get('agg') ?? undefined
  const [joiningVia, setJoiningVia] = useState<string | undefined>()
  // A coordinator's link brings in the one kind it was registered for - learners or tutors.
  const [linkAudience, setLinkAudience] = useState<'STUDENTS' | 'TUTORS' | undefined>()
  useEffect(() => {
    if (!aggregatorCode) return
    aggregatorPortalApi
      .onboardingName(aggregatorCode)
      .then((r) => {
        setJoiningVia(r.name)
        setLinkAudience(r.audience)
      })
      .catch(() => setJoiningVia(undefined))
  }, [aggregatorCode])
  const { token } = useAuth()
  const location = useLocation()
  const cameFromHandoff = (location.state as { inviteHandedOff?: boolean } | null)?.inviteHandedOff === true

  // Someone arriving from a parent invite is, by definition, registering as a guardian - asking
  // them to pick a role first would be asking a question the link already answered.
  const [role, setRole] = useState<RegisterRole | null>(inviteToken ? 'guardian' : null)

  // The invite link came from an email, so the mail client opened it in a new tab. If a signed-out
  // Yellow Dot tab is already open, the signup moves there and this tab steps aside - see
  // inviteHandoff. Offered once, and not by a tab that was itself handed the link.
  const [handedOff, setHandedOff] = useState(false)
  const offered = useRef(false)
  useEffect(() => {
    if (!inviteToken || token || cameFromHandoff || offered.current) return
    offered.current = true
    const path = `/register?invite=${encodeURIComponent(inviteToken)}`
    void offerInviteToOpenTab(path).then(setHandedOff)
  }, [inviteToken, token, cameFromHandoff])

  if (handedOff) {
    return (
      <OnboardingShell title="Continue in your other tab">
        <div className="space-y-4">
          <p className="text-sm text-ink-600">
            Your YellowDots tab that was already open now has the sign-up form. Carry on there.
          </p>
          <button type="button" onClick={() => setHandedOff(false)} className="btn-ghost">
            Continue in this tab instead
          </button>
        </div>
      </OnboardingShell>
    )
  }

  if (role === null) {
    return (
      <RoleSelectStep
        onSelect={setRole}
        joiningVia={joiningVia}
        only={
          !aggregatorCode
            ? undefined
            : linkAudience === 'STUDENTS'
              ? ['student']
              : linkAudience === 'TUTORS'
                ? ['tutor']
                : ['student', 'tutor']
        }
      />
    )
  }

  const onBack = () => setRole(null)

  if (role === 'student') {
    return <StudentRegisterForm onBack={onBack} referralCode={referralCode} aggregatorCode={aggregatorCode} />
  }
  if (role === 'tutor') {
    return <TutorRegisterForm onBack={onBack} referralCode={referralCode} aggregatorCode={aggregatorCode} />
  }

  // School, college and corporate share one four-step form driven by per-kind config - the entity
  // specs describe the same four steps three times, changing only labels and option lists.
  const institution = INSTITUTION_CONFIG[role]
  if (institution) {
    return <InstitutionRegisterForm config={institution} referralCode={referralCode} onBack={onBack} />
  }

  return (
    <GuardianRegisterForm onBack={onBack} referralCode={referralCode} inviteToken={inviteToken} />
  )
}
